/**
 * Utility to identify whether a meeting should be displayed in the meeting dashboard/activity views.
 * 
 * Requirement:
 * Scheduled calendar meetings must ONLY be shown if they have an actual score (> 0).
 * Unscored scheduled meetings (placeholders without evaluation) MUST be excluded.
 */
export const isMeetingRecorded = (m: any): boolean => {
  if (!m) return false;

  // Extract numeric score
  const scoreVal = Number(
    m.overallPercentage ??
    m.overallScore ??
    m.score ??
    m.avargerscroing ??
    m.scoreDetails?.overallScore ??
    m.ai_score?.overallScore ??
    m.ai_score?.overall_percentange ??
    m.ai_score?.overall_percentage ??
    0
  );
  const hasScore = !isNaN(scoreVal) && scoreVal > 0;

  // Determine if this is a scheduled meeting (from calendar sync or scheduled status)
  const statusStr = String(m.status || '').toLowerCase();
  const isScheduledStatus = statusStr === 'scheduled';
  const legacyId = String(m.legacyMongoId || '');
  const isCalOrLive = legacyId.startsWith('cal-') || legacyId.startsWith('live-');
  const isScheduledMeeting = isScheduledStatus || isCalOrLive;

  // RULE: Scheduled meetings are ONLY shown if they have received a score!
  if (isScheduledMeeting) {
    return hasScore;
  }

  // If already marked as recorded with a score
  if (hasScore) return true;

  // Media check for uploaded / recorded meetings
  const hasMedia = Boolean(
    (typeof m.uploaded_audio_url === 'string' && m.uploaded_audio_url.trim().length > 0) ||
    (typeof m.s3Url === 'string' && m.s3Url.trim().length > 0) ||
    (typeof m.recordingUrl === 'string' && m.recordingUrl.trim().length > 0) ||
    (typeof m.recording_url === 'string' && m.recording_url.trim().length > 0) ||
    (typeof m.audioUrl === 'string' && m.audioUrl.trim().length > 0) ||
    (typeof m.uploadedAudio === 'string' && m.uploadedAudio.trim().length > 0) ||
    (Array.isArray(m.fileUrls) && m.fileUrls.length > 0)
  );

  // Transcript check
  const hasTranscript = Boolean(
    (typeof m.transcript_text === 'string' && m.transcript_text.trim().length > 0) ||
    (Array.isArray(m.transcriptJson) && m.transcriptJson.length > 0)
  );

  // Check recording status
  const recStatus = String(m.recordingStatus || '').toUpperCase();
  if ((recStatus === 'PENDING' || recStatus === 'NOT_RECORDED') && !hasMedia && !hasTranscript) {
    return false;
  }

  // Active in-progress/processing states (e.g. newly uploaded audio being transcribed)
  if (
    statusStr.includes('process') ||
    statusStr.includes('transcrib') ||
    statusStr.includes('analyz') ||
    statusStr === 'in_progress' ||
    statusStr === 'uploading'
  ) {
    return true;
  }

  if (hasMedia || hasTranscript) return true;

  if (statusStr === 'completed') return true;

  return false;
};
